import React, { useState, Fragment } from "react";
import {
  Dialog,
  DialogPanel,
  DialogTitle,
  Transition,
} from "@headlessui/react";
import { XIcon } from "@heroicons/react/outline";
import ConfirmationDialog from "./ConfirmationDialog";

// Reusable sub-component for transitions
const TransitionChild = ({ children, ...props }) => (
  <Transition.Child {...props}>{children}</Transition.Child>
);

const Modal = ({
  isOpen,
  closeModal,
  title,
  children,
  isFullScreen,
  className = "",
  showConfirmationOnClose = false,
  confirmationMessage = "Are you sure you want to close? Unsaved changes will be lost.",
  maxWidth = "md", // 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | '5xl' | 'full'
  onConfirmClose = null,
  hideCloseButton = false,
}) => {
  const [showCloseConfirmation, setShowCloseConfirmation] = useState(false);

  // Map maxWidth prop to Tailwind classes
  const maxWidthClasses = {
    sm: "sm:max-w-sm",
    md: "sm:max-w-md",
    lg: "sm:max-w-lg",
    xl: "sm:max-w-xl",
    "2xl": "sm:max-w-2xl",
    "3xl": "sm:max-w-3xl",
    "4xl": "sm:max-w-4xl",
    "5xl": "sm:max-w-5xl",
    full: "sm:max-w-none",
  };

  // Determine the max-width class
  const maxWidthClass = isFullScreen
    ? maxWidthClasses.full
    : maxWidthClasses[maxWidth] || maxWidthClasses.md;

  const handleClose = () => {
    if (showConfirmationOnClose) {
      setShowCloseConfirmation(true);
    } else {
      closeModal();
    }
  };

  const handleConfirmClose = () => {
    setShowCloseConfirmation(false);
    closeModal();
    if (onConfirmClose) {
      onConfirmClose();
    }
  };

  return (
    <>
      <Transition appear show={isOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={handleClose}>
          {/* Backdrop */}
          <TransitionChild
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-30 backdrop-blur-sm" />
          </TransitionChild>

          {/* Container to center the panel */}
          <div className="fixed inset-0 flex items-center justify-center sm:p-4">
            {/* Wrapping in a TransitionChild for the panel animation */}
            <TransitionChild
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95 translate-y-4"
              enterTo="opacity-100 scale-100 translate-y-0"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100 translate-y-0"
              leaveTo="opacity-0 scale-95 translate-y-4"
            >
              <DialogPanel
                className={`
                  relative
                  w-full
                  h-full
                  sm:h-auto
                  transform
                  sm:rounded-3xl
                  p-6
                  text-left
                  shadow-2xl
                  transition-all
                  bg-glass-light
                  dark:bg-glass-dark
                  border border-neutral-light/20 dark:border-neutral-dark/20
                  backdrop-blur-lg
                  rounded-none
                  ${maxWidthClass}
                  ${
                    isFullScreen ? "sm:max-w-none w-full h-full sm:h-auto " : ""
                  }
                  ${className}
                  
                  /* Important for scrolling within the modal */
                  md:max-h-[calc(100vh-6rem)]
                  sm:max-h-[calc(100vh)]
                  overflow-y-auto
                `}
              >
                {/* Top Title & (optional) X-icon in normal flow */}
                {(title || !hideCloseButton) && (
                  <div className="mb-4 flex items-center justify-between">
                    {title && (
                      <DialogTitle
                        as="h3"
                        className="text-xl font-semibold text-text"
                      >
                        {title}
                      </DialogTitle>
                    )}

                    {!hideCloseButton && (
                      <button
                        onClick={handleClose}
                        className="text-text hover:text-primary-dark focus:outline-none"
                      >
                        <XIcon className="w-5 h-5" />
                      </button>
                    )}
                  </div>
                )}

                {/* The main body content */}
                <div className="text-text">{children}</div>

                {/* Bottom Close button (only if not hidden) */}
                {!hideCloseButton && (
                  <div className="mt-6 flex justify-end">
                    <button
                      type="button"
                      className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-text-on-primary shadow-md hover:bg-primary-dark focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 transition-colors"
                      onClick={handleClose}
                    >
                      Close
                    </button>
                  </div>
                )}
              </DialogPanel>
            </TransitionChild>
          </div>
        </Dialog>
      </Transition>

      {/* Confirmation Dialog if needed */}
      {showConfirmationOnClose && (
        <ConfirmationDialog
          isOpen={showCloseConfirmation}
          onConfirm={handleConfirmClose}
          onCancel={() => setShowCloseConfirmation(false)}
          message={confirmationMessage}
          confirmLabel="Yes, Close"
          cancelLabel="No, Cancel"
        />
      )}
    </>
  );
};

export default Modal;
